/**
 * Timezone helpers for entering/showing a wall-clock time in a SPECIFIC IANA
 * zone (the on-site location), independent of the viewer's browser zone (#1).
 *
 * Built on the platform `Intl` API only — no extra dependency. The
 * wall-time→UTC conversion is DST-safe via the standard two-pass offset method
 * (good for all normal times; the rare ambiguous/non-existent hour at a DST
 * transition resolves to one valid side, which is acceptable for scheduling).
 */
/** Offset (ms) of `tz` at the instant `utcMs`, i.e. localWallClock − utc. */
function zoneOffsetMs(utcMs, tz) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const p = {};
    for (const part of dtf.formatToParts(new Date(utcMs))) {
        if (part.type !== 'literal')
            p[part.type] = part.value;
    }
    const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
    return asUtc - utcMs;
}
/**
 * Interpret a `<input type="datetime-local">` value ("YYYY-MM-DDTHH:mm") as a
 * wall-clock time IN `tz` and return the corresponding UTC ISO string.
 * Returns null for empty/invalid input.
 */
export function zonedInputToUtcIso(local, tz) {
    if (!local)
        return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
    if (!m)
        return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const h = Number(m[4]);
    const mi = Number(m[5]);
    const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi);
    const offset = zoneOffsetMs(wallAsUtc, tz);
    let utc = wallAsUtc - offset;
    const offset2 = zoneOffsetMs(utc, tz); // refine across a DST boundary
    if (offset2 !== offset)
        utc = wallAsUtc - offset2;
    return new Date(utc).toISOString();
}
/**
 * Render a UTC ISO timestamp as the "YYYY-MM-DDTHH:mm" wall-clock value IN `tz`
 * (for seeding a datetime-local input). Returns '' for empty/invalid input.
 */
export function utcIsoToZonedInput(iso, tz) {
    if (!iso)
        return '';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime()))
        return '';
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
    const p = {};
    for (const part of dtf.formatToParts(dt)) {
        if (part.type !== 'literal')
            p[part.type] = part.value;
    }
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
/**
 * Friendly IANA → abbreviation, curated for the regions we operate in, split by
 * standard vs daylight time. The browser's own `short` label only spells out
 * letters for a few (mostly US) zones — for India/Europe/Gulf/Asia it returns a
 * numeric "GMT+5:30", which non-technical users don't recognise as a timezone.
 * This map gives the familiar IST / CET / CEST / GST … instead.
 *
 * Deliberately EXCLUDED: genuinely ambiguous abbreviations (Ireland & Israel
 * "IST", Bangladesh "BST", China & Taiwan "CST") — those fall through to the
 * browser's numeric offset rather than show a misleading set of letters.
 */
const FRIENDLY_TZ = {
    UTC: { std: 'UTC' },
    'Etc/UTC': { std: 'UTC' },
    'Asia/Kolkata': { std: 'IST' },
    'Europe/London': { std: 'GMT', dst: 'BST' },
    // Central Europe
    'Europe/Paris': { std: 'CET', dst: 'CEST' },
    'Europe/Berlin': { std: 'CET', dst: 'CEST' },
    'Europe/Madrid': { std: 'CET', dst: 'CEST' },
    'Europe/Rome': { std: 'CET', dst: 'CEST' },
    'Europe/Amsterdam': { std: 'CET', dst: 'CEST' },
    'Europe/Brussels': { std: 'CET', dst: 'CEST' },
    'Europe/Zurich': { std: 'CET', dst: 'CEST' },
    'Europe/Vienna': { std: 'CET', dst: 'CEST' },
    'Europe/Stockholm': { std: 'CET', dst: 'CEST' },
    'Europe/Oslo': { std: 'CET', dst: 'CEST' },
    'Europe/Copenhagen': { std: 'CET', dst: 'CEST' },
    'Europe/Warsaw': { std: 'CET', dst: 'CEST' },
    'Europe/Prague': { std: 'CET', dst: 'CEST' },
    'Europe/Budapest': { std: 'CET', dst: 'CEST' },
    // Eastern Europe
    'Europe/Athens': { std: 'EET', dst: 'EEST' },
    'Europe/Helsinki': { std: 'EET', dst: 'EEST' },
    'Europe/Bucharest': { std: 'EET', dst: 'EEST' },
    'Africa/Cairo': { std: 'EET', dst: 'EEST' },
    // Gulf / Middle East
    'Asia/Dubai': { std: 'GST' },
    'Asia/Muscat': { std: 'GST' },
    'Asia/Riyadh': { std: 'AST' },
    'Asia/Qatar': { std: 'AST' },
    'Asia/Kuwait': { std: 'AST' },
    'Asia/Bahrain': { std: 'AST' },
    // Asia-Pacific
    'Asia/Singapore': { std: 'SGT' },
    'Asia/Kuala_Lumpur': { std: 'MYT' },
    'Asia/Hong_Kong': { std: 'HKT' },
    'Asia/Tokyo': { std: 'JST' },
    'Asia/Seoul': { std: 'KST' },
    'Asia/Bangkok': { std: 'ICT' },
    'Asia/Ho_Chi_Minh': { std: 'ICT' },
    'Asia/Jakarta': { std: 'WIB' },
    'Asia/Manila': { std: 'PHT' },
    'Asia/Karachi': { std: 'PKT' },
    'Australia/Sydney': { std: 'AEST', dst: 'AEDT' },
    'Pacific/Auckland': { std: 'NZST', dst: 'NZDT' },
    // Africa
    'Africa/Lagos': { std: 'WAT' },
    'Africa/Nairobi': { std: 'EAT' },
    'Africa/Dar_es_Salaam': { std: 'EAT' },
    'Africa/Johannesburg': { std: 'SAST' },
    'Africa/Accra': { std: 'GMT' },
    // Americas
    'America/New_York': { std: 'EST', dst: 'EDT' },
    'America/Toronto': { std: 'EST', dst: 'EDT' },
    'America/Chicago': { std: 'CST', dst: 'CDT' },
    'America/Denver': { std: 'MST', dst: 'MDT' },
    'America/Los_Angeles': { std: 'PST', dst: 'PDT' },
    'America/Sao_Paulo': { std: 'BRT' },
};
/** The platform's own long zone name (DST-aware), e.g. "India Standard Time". */
export function zoneName(tz, iso) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'long',
        }).formatToParts(iso ? new Date(iso) : new Date());
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
    }
    catch {
        return tz;
    }
}
/**
 * Friendly short zone label (e.g. "CEST", "IST") for `tz` at `iso` (or now).
 * Uses the curated map (DST-aware via the platform's long zone name); anything
 * not mapped falls back to the browser's own label (an alphabetic abbreviation
 * like "EDT" where it has one, else the numeric "GMT+5:30").
 */
export function zoneAbbrev(tz, iso) {
    try {
        const at = iso ? new Date(iso) : new Date();
        const mapped = FRIENDLY_TZ[tz];
        if (mapped) {
            // The long name already reflects summer/daylight time → pick std vs dst.
            const isDaylight = /summer|daylight/i.test(zoneName(tz, iso));
            return isDaylight && mapped.dst ? mapped.dst : mapped.std;
        }
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'short',
        }).formatToParts(at);
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
    }
    catch {
        return tz;
    }
}
